import type { AdminReportListItemDto } from '@carcheck/shared';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatDate } from '../../../utils/format';
import { ReportAcceptanceBadge } from '../_components/ReportAcceptanceBadge';

/**
 * Column definitions for the reports moderation DataGrid (AUT-127),
 * extracted from the page so the page component stays under the line
 * cap. Sorting is disabled per column — the backend list is fixed
 * `submittedAt DESC` (architect 🟡 #3 — NOT createdAt), so the grid
 * never sends a sort the server can't honour.
 *
 * Columns mirror the mockup `reports-queue.html` MINUS the "Авто" column
 * (Igor decision — the wire DTO `AdminReportListItemDto` has no car
 * fields; a column would render a perpetual em-dash). Final ship: 6
 * columns instead of 7.
 *
 * Identity cell (inspector) renders through `<RedactedField perm=
 * "view_pii">`, which agrees with `row.piiRevealed` (both derive from
 * the same backend permission). A revealed value is never re-redacted;
 * only revealed values get a copy button.
 */

/** Inspector cell: avatar (2-letter initials) + name + truncated short id. */
const InspectorCell = ({ row, t }: { row: AdminReportListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700 }}>
      {initials(row.inspectorName)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      {row.inspectorName ? (
        <Typography variant="body2" fontWeight={600} noWrap component="span">
          <RedactedField
            perm="view_pii"
            value={row.inspectorName}
            redacted={row.inspectorName}
            {...(row.piiRevealed ? { copyable: true } : {})}
          />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
          {t('reports.detail.noValue')}
        </Typography>
      )}
    </Box>
  </Box>
);

/** Report-number + short id cell (mono font per mockup `.report-no` style). */
const ReportCell = ({ row }: { row: AdminReportListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
      {`№ ${row.reportNumber}`}
    </Typography>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
      {shortId(row.id)}
    </Typography>
  </Box>
);

/** Media-count cell: image icon + "N фото · M видео" via i18n plurals. */
const MediaCountCell = ({
  row,
  t,
}: {
  row: AdminReportListItemDto;
  t: TFunction;
}): ReactElement => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <ImageOutlinedIcon fontSize="small" sx={{ color: tokens.text.tertiary }} />
    <Typography variant="body2" fontFamily="monospace" sx={{ color: tokens.text.secondary }}>
      {t('reports.mediaCount.photo', { count: row.photoCount })}
      {' · '}
      {t('reports.mediaCount.video', { count: row.videoCount })}
    </Typography>
  </Box>
);

export const buildReportColumns = (t: TFunction): GridColDef<AdminReportListItemDto>[] => [
  {
    field: 'report',
    headerName: t('reports.columns.report'),
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => <ReportCell row={row} />,
  },
  {
    field: 'order',
    headerName: t('reports.columns.order'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {/* AUT-372 — an order-less (picker) report has no order number. */}
        {row.orderNumber === null ? '—' : `№ ${row.orderNumber}`}
      </Typography>
    ),
  },
  {
    field: 'inspector',
    headerName: t('reports.columns.inspector'),
    flex: 1.2,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => <InspectorCell row={row} t={t} />,
  },
  {
    field: 'media',
    headerName: t('reports.columns.media'),
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => <MediaCountCell row={row} t={t} />,
  },
  {
    field: 'submittedAt',
    headerName: t('reports.columns.finalizedAt'),
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    // `submittedAt` is only a finalize instant on a report the inspector
    // actually finalized — the column has a DB `@default(now())`, so on a
    // live `in_progress` draft it is merely the row's creation time. Now
    // that the default filter shows drafts, rendering it there would put
    // a plausible-looking lie in a date column, so drafts read "—".
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {row.status === 'in_progress' ? t('reports.detail.noValue') : formatDate(row.submittedAt)}
      </Typography>
    ),
  },
  {
    // AUT-284 — the acceptance read-out, NOT `row.status`. The status enum
    // never advances on acceptance, so a raw-status chip labelled every
    // finished report «ждёт заказчика», including ones already accepted,
    // paid out and released. Derived through `describeAcceptance`, the same
    // function the detail drawer's acceptance bar uses.
    field: 'acceptedAt',
    headerName: t('reports.columns.acceptance'),
    flex: 0.9,
    minWidth: 170,
    sortable: false,
    renderCell: ({ row }) => <ReportAcceptanceBadge report={row} />,
  },
];
